import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useLocalFilters } from "../../../../hooks/useLocalFilters";
import { makeRequest } from "../../../../services/api/client";
import { getOCELMetadata } from "../../../../services/api/ocel.api";
import { useOcelMetadata } from "../../../../stores/OcelContext";
import { getComparisonRange, clampRangeEndToToday } from "../../_shared/comparisonPeriod";
import { sanitizeThresholdFields } from "../utils/thresholdUtils";
import { getPresetRange, getBerlinToday, formatDateOnly } from "../../../../utils/berlinDate";

const NUMERIC_OCEL_TYPES = new Set([
  "float",
  "integer",
  "int",
  "double",
  "number",
]);

// Average/Median reference line: a fully independent data basis (own type +
// attribute, own reference period, own small filter subset), isolated from
// the tile's local filters and the global/tab filter.
const DEFAULT_REFERENCE_LINE = {
  enabled: false,
  type: "average", // "average" | "median"
  attributeSource: "object", // "object" | "event"
  objectType: null,
  objectAttribute: null,
  eventType: null,
  eventAttribute: null,
  periodStart: null,
  periodEnd: null,
  countNonWorkingDays: false,
  activityNodeFilters: { enabled: false, nodes: [], matchType: "any", mode: "include" },
  objectAttributeFilters: [],
};

/** Formats a Date to YYYY-MM-DD using local time (avoids UTC shift from toISOString). */
const fmtLocal = (dt) => {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const yesterdayISO = (now = new Date()) => getPresetRange("yesterday", now).endDate.slice(0, 10);

const normalizeSelectedDateForGranularity = (selectedDate, granularity) => {
  if (!selectedDate || granularity === "day") return selectedDate;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(selectedDate);
  if (!m) return selectedDate;

  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const pad2 = (n) => String(n).padStart(2, "0");

  if (granularity === "month") {
    return `${year}-${pad2(month)}-01`;
  }
  if (granularity === "year") {
    return `${year}-01-01`;
  }
  if (granularity === "week") {
    const dt = new Date(Date.UTC(year, month - 1, day));
    const mondayOffset = (dt.getUTCDay() + 6) % 7;
    dt.setUTCDate(dt.getUTCDate() - mondayOffset);
    return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
  }
  return selectedDate;
};

/**
 * Computes the display date range (start/end) for a given date and granularity.
 * Used by the comparison fetch to determine the current period boundaries.
 *
 * @param {string} dateStr - ISO date string (YYYY-MM-DD).
 * @param {string} gran - Granularity: "day", "week", "month", or "year".
 * @returns {{ startDate: string, endDate: string }} Range boundaries as ISO date strings.
 */
const computeDisplayRangeForHook = (dateStr, gran) => {
  if (!dateStr) return { startDate: "", endDate: "" };
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return { startDate: "", endDate: "" };

  if (gran === "day") return { startDate: fmtLocal(d), endDate: fmtLocal(d) };

  if (gran === "week") {
    const day = d.getDay();
    const mon = new Date(d);
    mon.setDate(d.getDate() - ((day + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return { startDate: fmtLocal(mon), endDate: fmtLocal(sun) };
  }

  if (gran === "month") {
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { startDate: fmtLocal(start), endDate: fmtLocal(end) };
  }

  return {
    startDate: `${d.getFullYear()}-01-01`,
    endDate: `${d.getFullYear()}-12-31`,
  };
};

/** Resolves a stored dateRange preset key to {startDate, endDate} (date-only strings),
 *  or null if the key is unknown/stale — defensive, since a stale localStorage snapshot
 *  could carry a preset key that no longer exists, and this must never throw (this exact
 *  class of unguarded-crash bug just broke this tile's Settings Apply button once already,
 *  see comparisonPeriod.js). */
const safeGetPresetRange = (key, now) => {
  try {
    const r = getPresetRange(key, now);
    return { startDate: r.startDate.slice(0, 10), endDate: r.endDate.slice(0, 10) };
  } catch {
    return null;
  }
};

/**
 * Resolves the Granularity-mode anchor date's default: the most recent date within
 * whichever period is configured — the Date Range Mode preset in Settings, falling back
 * to the tile's own Local Filter Date Range — or today (yesterday for Day granularity,
 * matching the existing convention) if neither is configured. Clamped to the dataset's
 * last known event date. Exported so the component can compute the same value
 * synchronously (e.g. right after a Settings preset change, before this hook's own
 * effect has had a chance to re-run).
 */
const resolveDefaultAnchor = ({
  dateRangePresetKey,
  localFilterDateRange,
  granularity,
  datasetLastDate,
  now = new Date(),
}) => {
  let resolved = null;

  if (dateRangePresetKey) {
    resolved = safeGetPresetRange(dateRangePresetKey, now)?.endDate ?? null;
  } else if (localFilterDateRange?.enabled && localFilterDateRange?.presetKey) {
    resolved = safeGetPresetRange(localFilterDateRange.presetKey, now)?.endDate ?? null;
  } else if (localFilterDateRange?.enabled && localFilterDateRange?.endDate) {
    resolved = String(localFilterDateRange.endDate).slice(0, 10);
  }

  if (!resolved) {
    resolved = granularity === "day" ? yesterdayISO(now) : formatDateOnly(getBerlinToday(now));
  }

  return datasetLastDate && resolved > datasetLastDate ? datasetLastDate : resolved;
};

const useAttributeSumLineChart = (activeFile, initialSettings = null) => {
  const {
    localFilters,
    updateLocalFilter,
    clearLocalFilters,
    hasLocalFilters,
    localFilterCount,
    api,
    currentFilters,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters,
  } = useLocalFilters(activeFile, initialSettings?.localFilters ?? null);
  const metadata = useOcelMetadata(activeFile);
  const configKey = `attributeSumLineChartConfig_${activeFile || "default"}`;
  const viewKey = `attributeSumLineChartView_${activeFile || "default"}`;

  // Data / loading state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // seriesData: { [attrName]: { chart_items, period_start, period_end, period_label, ... } }
  const [seriesData, setSeriesData] = useState(null);
  const [datasetLastDate, setDatasetLastDate] = useState(null);

  // Metadata state
  const [objectTypes, setObjectTypes] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [objectTypeNumericAttributes, setObjectTypeNumericAttributes] =
    useState({});

  const eventTypeNumericAttributes = useMemo(() => {
    if (!metadata?.event_types) return {};
    const result = {};
    for (const et of metadata.event_types) {
      result[et.name] = (et.attributes || [])
        .filter((a) => NUMERIC_OCEL_TYPES.has((a.type || "").toLowerCase()))
        .map((a) => a.name);
    }
    return result;
  }, [metadata]);

  const activityOptions = useMemo(
    () => Object.keys(metadata?.activity_types || {}).sort(),
    [metadata]
  );
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);

  // TileConfig (persistent, set in Edit Mode)
  const [title, setTitle] = useState("Attribute Sum Line Chart");
  const [attributeSource, setAttributeSource] = useState("object");
  const [selectedObjectTypes, setSelectedObjectTypes] = useState([]);
  const [selectedObjectAttributes, setSelectedObjectAttributes] = useState([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [selectedEventAttributes, setSelectedEventAttributes] = useState([]);
  const [enabledGranularities, setEnabledGranularities] = useState([
    "day",
    "week",
    "month",
    "year",
  ]);
  const [displayMode, setDisplayMode] = useState("aggregated"); // "aggregated" | "cumulative"
  const [valueAggregation, setValueAggregation] = useState("sum"); // "sum" | "average" | "median"
  const [timestampAnchor, setTimestampAnchor] = useState("last_event"); // "last_event" | "first_event" | "specific_activity"
  const [anchorActivity, setAnchorActivity] = useState(null); // string | null
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [comparisonMode, setComparisonMode] = useState("previousPeriod"); // "previousPeriod" | "yoy" | "previousYear"
  const [limitComparisonToCurrentProgress, setLimitComparisonToCurrentProgress] = useState(false);

  // Main line color (persistent, purely cosmetic — does not affect any API request/cache key)
  const [mainLineColor, setMainLineColor] = useState("#00a2ed");

  // Date Range Mode settings-preset key (e.g. "last30Days"), set via the Settings
  // "Date Range (Date Range Mode)" preset picker. TileConfig, not ViewState — this is
  // what must survive a refresh so a rolling preset can re-resolve fresh, instead of a
  // frozen dateRangeStart/dateRangeEnd/selectedDate pair going stale. See
  // resolveDefaultAnchor above.
  const [dateRangePresetKey, setDateRangePresetKey] = useState(null);

  // Threshold configuration (persistent, set in Edit Mode)
  const [thresholdType, setThresholdType] = useState(null); // null | "more_the_better" | "less_the_better" | "optimal_range"
  const [thresholdTarget, setThresholdTarget] = useState(null);
  const [thresholdWarningPercentage, setThresholdWarningPercentage] =
    useState(null);
  const [thresholdTargetPerDay, setThresholdTargetPerDay] = useState(null);
  const [thresholdLowerThreshold, setThresholdLowerThreshold] = useState(null);
  const [thresholdUpperThreshold, setThresholdUpperThreshold] = useState(null);
  const [thresholdLowerThresholdPerDay, setThresholdLowerThresholdPerDay] =
    useState(null);
  const [thresholdUpperThresholdPerDay, setThresholdUpperThresholdPerDay] =
    useState(null);

  // Pre-computed threshold data from backend (zones, reference lines, colors)
  const [thresholdData, setThresholdData] = useState(null);

  // Average/Median reference line config (persistent) + pre-computed data from backend
  const [referenceLine, setReferenceLine] = useState(DEFAULT_REFERENCE_LINE);
  const [referenceLineData, setReferenceLineData] = useState(null);

  // Comparison data state
  const [comparisonSeriesData, setComparisonSeriesData] = useState(null);

  // ViewState (runtime, set in View Mode)
  const [granularity, setGranularity] = useState("day");
  const [selectedDate, setSelectedDate] = useState(yesterdayISO());
  const [viewMode, setViewMode] = useState("granularity");
  const [dateRangeStart, setDateRangeStart] = useState(yesterdayISO());
  const [dateRangeEnd, setDateRangeEnd] = useState(yesterdayISO());

  // Request dedup ref
  const inFlightSignatureRef = useRef(null);
  const lastResolvedSignatureRef = useRef(null);
  const lastResolvedAtRef = useRef(0);
  const latestRequestIdRef = useRef(0);

  // Restore TileConfig from localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(configKey);
      if (!raw) return;
      const cfg = JSON.parse(raw);

      if (typeof cfg.title === "string") setTitle(cfg.title);
      if (cfg.attributeSource === "object" || cfg.attributeSource === "event")
        setAttributeSource(cfg.attributeSource);
      if (Array.isArray(cfg.selectedObjectTypes))
        setSelectedObjectTypes(cfg.selectedObjectTypes);
      if (Array.isArray(cfg.selectedObjectAttributes))
        setSelectedObjectAttributes(cfg.selectedObjectAttributes);
      if (Array.isArray(cfg.selectedEventTypes))
        setSelectedEventTypes(cfg.selectedEventTypes);
      if (Array.isArray(cfg.selectedEventAttributes))
        setSelectedEventAttributes(cfg.selectedEventAttributes);
      if (
        Array.isArray(cfg.enabledGranularities) &&
        cfg.enabledGranularities.length > 0
      )
        setEnabledGranularities(cfg.enabledGranularities);
      if (cfg.displayMode === "aggregated" || cfg.displayMode === "cumulative")
        setDisplayMode(cfg.displayMode);
      if (["sum", "average", "median"].includes(cfg.valueAggregation))
        setValueAggregation(cfg.valueAggregation);
      if (["last_event", "first_event", "specific_activity"].includes(cfg.timestampAnchor))
        setTimestampAnchor(cfg.timestampAnchor);
      if (cfg.anchorActivity !== undefined) setAnchorActivity(cfg.anchorActivity);
      if (typeof cfg.comparisonEnabled === "boolean")
        setComparisonEnabled(cfg.comparisonEnabled);
      if (typeof cfg.comparisonMode === "string") {
        // Legacy "previousYear" mode (year-only) folds into "previousPeriod"
        // since the option list is now uniform across all granularities.
        const migrated =
          cfg.comparisonMode === "previousYear"
            ? "previousPeriod"
            : cfg.comparisonMode;
        setComparisonMode(migrated);
      }
      if (typeof cfg.limitComparisonToCurrentProgress === "boolean")
        setLimitComparisonToCurrentProgress(cfg.limitComparisonToCurrentProgress);
      if (cfg.dateRangePresetKey !== undefined) setDateRangePresetKey(cfg.dateRangePresetKey);
      // Threshold config — project onto the loaded type so stale cross-type values
      // from older localStorage snapshots don't revive.
      const loadedThresholdType =
        cfg.thresholdType === "more_the_better" ||
        cfg.thresholdType === "less_the_better" ||
        cfg.thresholdType === "optimal_range"
          ? cfg.thresholdType
          : null;
      setThresholdType(loadedThresholdType);
      const sanitized = sanitizeThresholdFields(cfg, loadedThresholdType);
      setThresholdTarget(sanitized.thresholdTarget);
      setThresholdWarningPercentage(sanitized.thresholdWarningPercentage);
      setThresholdTargetPerDay(sanitized.thresholdTargetPerDay);
      setThresholdLowerThreshold(sanitized.thresholdLowerThreshold);
      setThresholdUpperThreshold(sanitized.thresholdUpperThreshold);
      setThresholdLowerThresholdPerDay(sanitized.thresholdLowerThresholdPerDay);
      setThresholdUpperThresholdPerDay(sanitized.thresholdUpperThresholdPerDay);
      if (cfg.referenceLine && typeof cfg.referenceLine === "object") {
        setReferenceLine({ ...DEFAULT_REFERENCE_LINE, ...cfg.referenceLine });
      }
      if (typeof cfg.mainLineColor === "string")
        setMainLineColor(cfg.mainLineColor);
    } catch {
      // ignore
    }
  }, [configKey]);

  // Restore ViewState from localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(viewKey);
      if (!raw) return;
      const vs = JSON.parse(raw);

      if (typeof vs.granularity === "string") setGranularity(vs.granularity);
      // selectedDate is deliberately NOT restored here — it's re-derived fresh on every
      // mount by the resolver effect below (from dateRangePresetKey / Local Filter Date
      // Range / today), which is the whole point of this feature: only TileConfig, not a
      // frozen ViewState snapshot, survives a refresh as the anchor's source of truth.
      if (vs.viewMode === "granularity" || vs.viewMode === "daterange")
        setViewMode(vs.viewMode);
      if (typeof vs.dateRangeStart === "string")
        setDateRangeStart(vs.dateRangeStart);
      if (typeof vs.dateRangeEnd === "string") setDateRangeEnd(vs.dateRangeEnd);
    } catch {
      // ignore
    }
  }, [viewKey]);

  // Restore from centralised config (overrides localStorage on mount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    const s = initialSettings;
    if (s.title !== undefined) setTitle(s.title);
    if (s.attributeSource !== undefined) setAttributeSource(s.attributeSource);
    if (s.selectedObjectTypes !== undefined) setSelectedObjectTypes(s.selectedObjectTypes);
    if (s.selectedObjectAttributes !== undefined) setSelectedObjectAttributes(s.selectedObjectAttributes);
    if (s.selectedEventTypes !== undefined) setSelectedEventTypes(s.selectedEventTypes);
    if (s.selectedEventAttributes !== undefined) setSelectedEventAttributes(s.selectedEventAttributes);
    if (s.enabledGranularities !== undefined) setEnabledGranularities(s.enabledGranularities);
    if (s.displayMode !== undefined) setDisplayMode(s.displayMode);
    if (s.valueAggregation !== undefined) setValueAggregation(s.valueAggregation);
    if (s.timestampAnchor !== undefined) setTimestampAnchor(s.timestampAnchor);
    if (s.anchorActivity !== undefined) setAnchorActivity(s.anchorActivity);
    if (s.comparisonEnabled !== undefined) setComparisonEnabled(s.comparisonEnabled);
    if (s.comparisonMode !== undefined) setComparisonMode(s.comparisonMode);
    if (s.limitComparisonToCurrentProgress !== undefined)
      setLimitComparisonToCurrentProgress(s.limitComparisonToCurrentProgress);
    if (s.dateRangePresetKey !== undefined) setDateRangePresetKey(s.dateRangePresetKey);
    if (s.thresholdType !== undefined) setThresholdType(s.thresholdType);
    if (s.thresholdTarget !== undefined) setThresholdTarget(s.thresholdTarget);
    if (s.thresholdWarningPercentage !== undefined) setThresholdWarningPercentage(s.thresholdWarningPercentage);
    if (s.thresholdTargetPerDay !== undefined) setThresholdTargetPerDay(s.thresholdTargetPerDay);
    if (s.thresholdLowerThreshold !== undefined) setThresholdLowerThreshold(s.thresholdLowerThreshold);
    if (s.thresholdUpperThreshold !== undefined) setThresholdUpperThreshold(s.thresholdUpperThreshold);
    if (s.thresholdLowerThresholdPerDay !== undefined) setThresholdLowerThresholdPerDay(s.thresholdLowerThresholdPerDay);
    if (s.thresholdUpperThresholdPerDay !== undefined) setThresholdUpperThresholdPerDay(s.thresholdUpperThresholdPerDay);
    if (s.granularity !== undefined) setGranularity(s.granularity);
    // selectedDate intentionally not restored — see the matching note on the
    // localStorage ViewState restore effect above.
    if (s.viewMode !== undefined) setViewMode(s.viewMode);
    if (s.dateRangeStart !== undefined) setDateRangeStart(s.dateRangeStart);
    if (s.dateRangeEnd !== undefined) setDateRangeEnd(s.dateRangeEnd);
    if (s.referenceLine !== undefined && s.referenceLine !== null) {
      setReferenceLine({ ...DEFAULT_REFERENCE_LINE, ...s.referenceLine });
    }
    if (s.mainLineColor !== undefined) setMainLineColor(s.mainLineColor);
  }, []);

  // Persist TileConfig
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        configKey,
        JSON.stringify({
          title,
          attributeSource,
          selectedObjectTypes,
          selectedObjectAttributes,
          selectedEventTypes,
          selectedEventAttributes,
          enabledGranularities,
          displayMode,
          valueAggregation,
          timestampAnchor,
          anchorActivity,
          comparisonEnabled,
          comparisonMode,
          limitComparisonToCurrentProgress,
          dateRangePresetKey,
          thresholdType,
          thresholdTarget,
          thresholdWarningPercentage,
          thresholdTargetPerDay,
          thresholdLowerThreshold,
          thresholdUpperThreshold,
          thresholdLowerThresholdPerDay,
          thresholdUpperThresholdPerDay,
          referenceLine,
          mainLineColor,
        }),
      );
    } catch {
      /* no-op */
    }
  }, [
    configKey,
    title,
    attributeSource,
    selectedObjectTypes,
    selectedObjectAttributes,
    selectedEventTypes,
    selectedEventAttributes,
    enabledGranularities,
    displayMode,
    valueAggregation,
    timestampAnchor,
    anchorActivity,
    comparisonEnabled,
    comparisonMode,
    limitComparisonToCurrentProgress,
    dateRangePresetKey,
    thresholdType,
    thresholdTarget,
    thresholdWarningPercentage,
    thresholdTargetPerDay,
    thresholdLowerThreshold,
    thresholdUpperThreshold,
    thresholdLowerThresholdPerDay,
    thresholdUpperThresholdPerDay,
    referenceLine,
    mainLineColor,
  ]);

  // Persist ViewState
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        viewKey,
        JSON.stringify({
          granularity,
          selectedDate,
          viewMode,
          dateRangeStart,
          dateRangeEnd,
        }),
      );
    } catch {
      /* no-op */
    }
  }, [
    viewKey,
    granularity,
    selectedDate,
    viewMode,
    dateRangeStart,
    dateRangeEnd,
  ]);

  // Resolve the Granularity-mode anchor date (and, if a Date-Range-Mode preset is
  // configured, the Date Range Mode's own start/end fields) whenever the configured
  // period changes, or on mount/reload — this is what makes a rolling preset ("last 30
  // days") track forward instead of freezing at whatever date it first resolved to.
  // datasetLastDate is deliberately NOT a dependency: it's a data-availability clamp,
  // not a "the configured period changed" signal, and re-triggering on it would wipe a
  // manual edit the user made after metadata last reloaded — the existing clamp
  // call-sites (fetchMetadata, handleGranularityChange, handleViewModeChange) already
  // handle that separately, by clamping whatever is currently selected rather than
  // recomputing the default from scratch.
  useEffect(() => {
    setSelectedDate(
      resolveDefaultAnchor({
        dateRangePresetKey,
        localFilterDateRange: localFilters?.dateRange,
        granularity,
        datasetLastDate,
      }),
    );
    if (dateRangePresetKey) {
      const resolved = safeGetPresetRange(dateRangePresetKey, new Date());
      if (resolved) {
        setDateRangeStart(resolved.startDate);
        setDateRangeEnd(resolved.endDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRangePresetKey,
    granularity,
    localFilters?.dateRange?.enabled,
    localFilters?.dateRange?.presetKey,
    localFilters?.dateRange?.endDate,
  ]);

  // Fetch numeric attributes for selected object types (schema-based, like AggregationKPI)
  const fetchNumericAttributesForObjectTypes = useCallback(
    async (types) => {
      if (!activeFile || !types.length) return;
      try {
        const results = await Promise.all(
          types.map((type) =>
            makeRequest(
              "/ocel/object-type-attributes",
              { params: { file_type: activeFile, object_type: type } },
              "object type attributes",
            ),
          ),
        );
        setObjectTypeNumericAttributes((prev) => {
          const next = { ...prev };
          types.forEach((type, i) => {
            next[type] = results[i]?.numeric_attributes || [];
          });
          return next;
        });
      } catch {
        // ignore — keeps existing values
      }
    },
    [activeFile],
  );

  const fetchMetadata = useCallback(async () => {
    if (!activeFile) {
      setError("No active file selected");
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const metadataRes = await api.post(
        "/kpi/attribute-sum-bar-chart/metadata",
        { file_type: activeFile },
        {},
        "Attribute Sum Metadata",
      );

      setObjectTypes(metadataRes.object_types || []);
      setEventTypes(metadataRes.event_types || []);

      // Store numeric attributes per object type from metadata (avoids per-type API calls)
      if (metadataRes.object_type_numeric_attributes) {
        setObjectTypeNumericAttributes(
          metadataRes.object_type_numeric_attributes,
        );
      }

      try {
        const dateMetaRes = await getOCELMetadata(activeFile);
        if (dateMetaRes?.date_range?.end) {
          const lastDate = dateMetaRes.date_range.end.slice(0, 10);
          setDatasetLastDate(lastDate);
          setSelectedDate((prev) => (prev > lastDate ? lastDate : prev));
        }
      } catch {
        // Non-critical
      }

      setIsMetadataLoaded(true);
      return true;
    } catch (err) {
      setError(err.message || "Failed to load metadata");
      setIsMetadataLoaded(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, [activeFile, api]);

  // Fetch chart data (one call per attribute for per-attribute lines)
  const fetchChartData = useCallback(
    async (overrides = {}) => {
      const effectiveAttributeSource =
        overrides.attributeSource ?? attributeSource;
      const effectiveSelectedObjectTypes =
        overrides.selectedObjectTypes ?? selectedObjectTypes;
      const effectiveSelectedObjectAttributes =
        overrides.selectedObjectAttributes ?? selectedObjectAttributes;
      const effectiveSelectedEventTypes =
        overrides.selectedEventTypes ?? selectedEventTypes;
      const effectiveSelectedEventAttributes =
        overrides.selectedEventAttributes ?? selectedEventAttributes;
      const effectiveGranularity = overrides.granularity ?? granularity;
      const effectiveEnabledGranularities =
        overrides.enabledGranularities ?? enabledGranularities;
      const effectiveSelectedDate = overrides.selectedDate ?? selectedDate;
      const normalizedGranularityDate = normalizeSelectedDateForGranularity(
        effectiveSelectedDate,
        effectiveGranularity,
      );
      const effectiveViewMode = overrides.viewMode ?? viewMode;
      const effectiveDateRangeStart =
        overrides.dateRangeStart ?? dateRangeStart;
      const effectiveDateRangeEnd = overrides.dateRangeEnd ?? dateRangeEnd;
      const effectiveReferenceLine = overrides.referenceLine ?? referenceLine;

      if (!activeFile) {
        setError("No active file selected");
        return false;
      }

      // Determine which attributes to fetch
      const isObjectMode = effectiveAttributeSource === "object";
      const selectedTypes = isObjectMode
        ? effectiveSelectedObjectTypes
        : effectiveSelectedEventTypes;
      const selectedAttrs = isObjectMode
        ? effectiveSelectedObjectAttributes
        : effectiveSelectedEventAttributes;

      if (!selectedTypes.length) {
        setError(
          `Please select at least one ${isObjectMode ? "object" : "event"} type`,
        );
        return false;
      }
      if (!selectedAttrs.length) {
        setError(
          `Please select at least one ${isObjectMode ? "object" : "event"} attribute`,
        );
        return false;
      }

      const isDateRange = effectiveViewMode === "daterange";
      if (!isDateRange && !effectiveSelectedDate) {
        setError("Please select a date");
        return false;
      }
      if (isDateRange && (!effectiveDateRangeStart || !effectiveDateRangeEnd)) {
        setError("Please select start and end dates");
        return false;
      }

      // Build payload with group_by_attribute for per-attribute breakdowns
      const attrKey = isObjectMode
        ? "selected_object_attributes"
        : "selected_event_attributes";
      const clearKey = isObjectMode
        ? "selected_event_attributes"
        : "selected_object_attributes";

      // Compute comparison period dates if comparison is enabled (granularity mode only)
      const effectiveComparisonEnabled =
        overrides.comparisonEnabled ?? comparisonEnabled;
      const effectiveComparisonMode =
        overrides.comparisonMode ?? comparisonMode;
      const effectiveLimitComparisonToCurrentProgress =
        overrides.limitComparisonToCurrentProgress ?? limitComparisonToCurrentProgress;
      let comparisonParams = {};
      if (effectiveComparisonEnabled && !isDateRange) {
        let currentRange;
        if (isDateRange) {
          currentRange = {
            start: effectiveDateRangeStart,
            end: effectiveDateRangeEnd,
          };
        } else {
          const range = computeDisplayRangeForHook(
            normalizedGranularityDate,
            effectiveGranularity,
          );
          currentRange = { start: range.startDate, end: range.endDate };
        }
        if (effectiveLimitComparisonToCurrentProgress) {
          // Clamp the CURRENT period's end to today before deriving the
          // comparison range — getComparisonRange anchors the comparison
          // period's end to this range's end, so a still-in-progress
          // year/month/week compares against the SAME elapsed portion of the
          // prior period instead of its full, already-complete length.
          currentRange = clampRangeEndToToday(
            currentRange,
            effectiveGranularity,
            formatDateOnly(getBerlinToday()),
          );
        }
        const compRange = getComparisonRange(
          effectiveComparisonMode,
          effectiveGranularity,
          currentRange,
        );
        if (compRange.start && compRange.end) {
          comparisonParams = {
            comparison_period_start: compRange.start,
            comparison_period_end: compRange.end,
          };
        }
      }

      const payload = {
        file_type: activeFile,
        attribute_source: effectiveAttributeSource,
        aggregation_mode: "accumulated",
        value_aggregation: overrides.valueAggregation ?? valueAggregation,
        timestamp_anchor: overrides.timestampAnchor ?? timestampAnchor,
        anchor_activity: overrides.anchorActivity ?? anchorActivity,
        [attrKey]: selectedAttrs,
        [clearKey]: [],
        ...(isObjectMode
          ? {
              selected_object_types: effectiveSelectedObjectTypes,
              selected_event_types: [],
            }
          : {
              selected_event_types: effectiveSelectedEventTypes,
              selected_object_types: [],
            }),
        ...(isDateRange
          ? {
              period_start: effectiveDateRangeStart,
              period_end: effectiveDateRangeEnd,
              granularity: "day",
              selected_date: effectiveDateRangeStart,
            }
          : {
              granularity: effectiveGranularity,
              selected_date: normalizedGranularityDate,
              enabled_granularities: effectiveEnabledGranularities,
            }),
        group_by_attribute: true,
        display_mode: overrides.displayMode ?? displayMode,
        ...comparisonParams,
        // Threshold config
        ...((overrides.thresholdType ?? thresholdType)
          ? {
              threshold_type: overrides.thresholdType ?? thresholdType,
              threshold_target: overrides.thresholdTarget ?? thresholdTarget,
              threshold_warning_percentage:
                overrides.thresholdWarningPercentage ??
                thresholdWarningPercentage,
              threshold_target_per_day:
                overrides.thresholdTargetPerDay ?? thresholdTargetPerDay,
              threshold_lower:
                overrides.thresholdLowerThreshold ?? thresholdLowerThreshold,
              threshold_upper:
                overrides.thresholdUpperThreshold ?? thresholdUpperThreshold,
              threshold_lower_per_day:
                overrides.thresholdLowerThresholdPerDay ??
                thresholdLowerThresholdPerDay,
              threshold_upper_per_day:
                overrides.thresholdUpperThresholdPerDay ??
                thresholdUpperThresholdPerDay,
            }
          : {}),
        // Average/Median reference line — a fully independent data basis, isolated
        // from the tile's own local filters and the global/tab filter.
        ...(effectiveReferenceLine?.enabled
          ? {
              reference_line: {
                enabled: true,
                type: effectiveReferenceLine.type,
                attribute_source: effectiveReferenceLine.attributeSource,
                object_type:
                  effectiveReferenceLine.attributeSource === "object"
                    ? effectiveReferenceLine.objectType
                    : null,
                object_attribute:
                  effectiveReferenceLine.attributeSource === "object"
                    ? effectiveReferenceLine.objectAttribute
                    : null,
                event_type:
                  effectiveReferenceLine.attributeSource === "event"
                    ? effectiveReferenceLine.eventType
                    : null,
                event_attribute:
                  effectiveReferenceLine.attributeSource === "event"
                    ? effectiveReferenceLine.eventAttribute
                    : null,
                period_start: effectiveReferenceLine.periodStart,
                period_end: effectiveReferenceLine.periodEnd,
                count_non_working_days: !!effectiveReferenceLine.countNonWorkingDays,
                activity_node_filters: effectiveReferenceLine.activityNodeFilters?.enabled
                  ? {
                      enabled: true,
                      nodes: effectiveReferenceLine.activityNodeFilters.nodes || [],
                      match_type: effectiveReferenceLine.activityNodeFilters.matchType || "any",
                      mode: effectiveReferenceLine.activityNodeFilters.mode || "include",
                    }
                  : undefined,
                object_attribute_filters:
                  (effectiveReferenceLine.objectAttributeFilters || []).filter((f) => f.enabled)
                    .length > 0
                    ? effectiveReferenceLine.objectAttributeFilters.filter((f) => f.enabled)
                    : undefined,
              },
            }
          : {}),
      };

      // Include currentFilters (the local/global filter set api.post() actually merges into
      // the request under the hood) so a filter-only change produces a different signature —
      // otherwise it's invisible to this dedup check and can get silently swallowed.
      const requestSignature = JSON.stringify({ payload, currentFilters });

      if (inFlightSignatureRef.current === requestSignature) return true;
      const now = Date.now();
      if (
        lastResolvedSignatureRef.current === requestSignature &&
        now - lastResolvedAtRef.current < 1000
      ) {
        return true;
      }

      setLoading(true);
      setError(null);
      const requestId = ++latestRequestIdRef.current;

      try {
        inFlightSignatureRef.current = requestSignature;

        const response = await api.post(
          "/kpi/attribute-sum-bar-chart",
          payload,
          {},
          "Attribute Sum Line Chart",
        );

        if (requestId !== latestRequestIdRef.current) return true;

        // Build seriesData from grouped_series (per-attribute breakdowns)
        const grouped = response.grouped_series;
        if (grouped && Object.keys(grouped).length > 0) {
          const series = {};
          for (const [attr, data] of Object.entries(grouped)) {
            series[attr] = {
              chart_items: data.chart_items,
              total_value: data.total_value,
              period_start: response.period_start,
              period_end: response.period_end,
              period_label: response.period_label,
            };
          }
          setSeriesData(series);
        } else {
          setSeriesData(null);
        }

        // Extract comparison data from the same response
        const compGrouped = response.comparison_grouped_series;
        if (compGrouped && Object.keys(compGrouped).length > 0) {
          const compSeries = {};
          for (const [attr, data] of Object.entries(compGrouped)) {
            compSeries[attr] = {
              chart_items: data.chart_items,
              total_value: data.total_value,
              period_start: response.comparison_period_start,
              period_end: response.comparison_period_end,
            };
          }
          setComparisonSeriesData(compSeries);
        } else {
          setComparisonSeriesData(null);
        }

        // Extract pre-computed threshold data (zones + reference lines)
        setThresholdData(response.threshold_data || null);

        // Extract pre-computed Average/Median reference line data
        setReferenceLineData(response.reference_line_data || null);

        lastResolvedSignatureRef.current = requestSignature;
        lastResolvedAtRef.current = Date.now();
        return true;
      } catch (err) {
        if (requestId !== latestRequestIdRef.current) return false;
        setSeriesData(null);
        setComparisonSeriesData(null);
        setThresholdData(null);
        setReferenceLineData(null);
        let msg = err?.message || "Failed to load chart data";
        if (msg.includes("404")) {
          msg = "Endpoint not found. Please check if the backend is running.";
        } else if (msg.includes("400")) {
          msg = "Invalid configuration. Please check your settings.";
        }
        setError(msg);
        return false;
      } finally {
        if (inFlightSignatureRef.current === requestSignature) {
          inFlightSignatureRef.current = null;
        }
        if (requestId === latestRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [
      activeFile,
      api,
      currentFilters,
      attributeSource,
      selectedObjectTypes,
      selectedObjectAttributes,
      selectedEventTypes,
      selectedEventAttributes,
      enabledGranularities,
      granularity,
      selectedDate,
      viewMode,
      dateRangeStart,
      comparisonEnabled,
      comparisonMode,
      limitComparisonToCurrentProgress,
      dateRangeEnd,
      displayMode,
      valueAggregation,
      timestampAnchor,
      anchorActivity,
      thresholdType,
      thresholdTarget,
      thresholdWarningPercentage,
      thresholdTargetPerDay,
      thresholdLowerThreshold,
      thresholdUpperThreshold,
      thresholdLowerThresholdPerDay,
      thresholdUpperThresholdPerDay,
      referenceLine,
    ],
  );

  // Reset on file change
  useEffect(() => {
    setSeriesData(null);
    setComparisonSeriesData(null);
    setThresholdData(null);
    setReferenceLineData(null);
    setError(null);
    setIsMetadataLoaded(false);
    setObjectTypes([]);
    setEventTypes([]);
    setObjectTypeNumericAttributes({});
    setDatasetLastDate(null);
  }, [activeFile]);

  return {
    // Data
    loading,
    error,
    seriesData,
    datasetLastDate,
    // Metadata
    objectTypes,
    eventTypes,
    objectTypeNumericAttributes,
    eventTypeNumericAttributes,
    isMetadataLoaded,
    // TileConfig
    title,
    attributeSource,
    selectedObjectTypes,
    selectedObjectAttributes,
    selectedEventTypes,
    selectedEventAttributes,
    enabledGranularities,
    displayMode,
    valueAggregation,
    timestampAnchor,
    anchorActivity,
    activityOptions,
    comparisonEnabled,
    comparisonMode,
    limitComparisonToCurrentProgress,
    comparisonSeriesData,
    mainLineColor,
    // Threshold config
    thresholdType,
    thresholdTarget,
    thresholdWarningPercentage,
    thresholdTargetPerDay,
    thresholdLowerThreshold,
    thresholdUpperThreshold,
    thresholdLowerThresholdPerDay,
    thresholdUpperThresholdPerDay,
    thresholdData,
    // Reference line
    referenceLine,
    referenceLineData,
    // ViewState
    granularity,
    selectedDate,
    viewMode,
    dateRangeStart,
    dateRangeEnd,
    dateRangePresetKey,
    // TileConfig setters
    setTitle,
    setAttributeSource,
    setSelectedObjectTypes,
    setSelectedObjectAttributes,
    setSelectedEventTypes,
    setSelectedEventAttributes,
    setEnabledGranularities,
    setDisplayMode,
    setValueAggregation,
    setTimestampAnchor,
    setAnchorActivity,
    setComparisonEnabled,
    setComparisonMode,
    setLimitComparisonToCurrentProgress,
    setMainLineColor,
    // Threshold config setters
    setThresholdType,
    setThresholdTarget,
    setThresholdWarningPercentage,
    setThresholdTargetPerDay,
    setThresholdLowerThreshold,
    setThresholdUpperThreshold,
    setThresholdLowerThresholdPerDay,
    setThresholdUpperThresholdPerDay,
    // Reference line setter
    setReferenceLine,
    // ViewState setters
    setGranularity,
    setSelectedDate,
    setViewMode,
    setDateRangeStart,
    setDateRangeEnd,
    setDateRangePresetKey,
    // Local filters
    localFilters,
    updateLocalFilter,
    clearLocalFilters,
    hasLocalFilters,
    localFilterCount,
    currentFilters,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters,
    // Actions
    fetchMetadata,
    fetchChartData,
    fetchNumericAttributesForObjectTypes,
  };
};

export { useAttributeSumLineChart, resolveDefaultAnchor };
